/* eslint-disable @typescript-eslint/no-use-before-define */

import {
  CohortItem,
  isEventBasedType,
  isInlineCohortItem,
  isUserBasedType,
  QueryCohortEntry,
  QueryEntry,
  QueryEventEntry,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {ResourceType} from 'common/report/constants';
import {dataGroupIDsEqual} from 'common/util/index';
import {hasUserFilters} from 'common/util/custom_events';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {
  CohortSegment,
  isCohortSegment,
  isCohortSegmentItem,
  isInlineCohortSegment,
  isUserSegment,
  Segment,
} from 'common/query-builder/query-block/segment-menu/types';
import type {CustomEventDefinition} from 'common/report/util/saved-entities/types';
import {reporter} from 'common/report/error-reporting';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {isBehavioralCustomProperty} from 'common/widgets/custom-property-modal/util';
import {isBehaviorSegmentation} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {Segmentation} from 'common/types/reports/query';

import {QueryBuilderSelection} from '../types';

/**
 * Removes invalid entries for the selected data group and updates dataGroupId fields on the remaining entries
 */
export function setQueryDataGroup({
  query,
  dataGroupId,
  customEvents,
}: {
  query: QueryBuilderSelection;
  dataGroupId?: string;
  customEvents: CustomEventDefinition[];
}): QueryBuilderSelection {
  const sanitizedQuery = sanitizeQueryForDataGroup({query, dataGroupId, customEvents});
  return setQueryDataGroupProps({query: sanitizedQuery, dataGroupId});
}

/**
 * Returns true if the query has params that would be removed when switching to a given data group
 */
export function queryIsInvalidForDataGroup({
  query,
  dataGroupId,
  customEvents,
}: {
  query: QueryBuilderSelection;
  dataGroupId?: string;
  customEvents: CustomEventDefinition[];
}): boolean {
  return (
    // validate main entries
    query.entries.some((queryEntry) => queryEntryIsInvalidForDataGroup({queryEntry, dataGroupId, customEvents})) ||
    // validate funnel exclusion steps
    query.exclusionSteps.some((queryEntry) =>
      queryEntryIsInvalidForDataGroup({queryEntry, dataGroupId, customEvents}),
    ) ||
    // validate global filters
    query.filters.some((filter) => filterIsInvalidForDataGroup({filter, dataGroupId, customEvents})) ||
    // validate global breakdowns
    groupByIsInvalidForDataGroup(query.groupBy, dataGroupId)
  );
}

export function isCustomEventWithUserFilters(
  eventEntry: QueryEventEntry,
  customEvents: CustomEventDefinition[],
): boolean {
  if (!eventEntry.event.custom) {
    return false;
  }
  if (!customEvents) {
    reporter.error(`customEvents argument missing`);
    return true;
  }

  const customEvent = customEvents.find((ce) => ce.id === eventEntry.event.id);
  return !customEvent || hasUserFilters(customEvent, customEvents);
}

export function cohortEntryIsInvalidForDataGroup(item: QueryCohortEntry | CohortSegment, dataGroupId?: string) {
  if (!isCohortSegmentItem(item)) {
    item = convertCohortItemToCohortSegment(item.event);
  }
  return !dataGroupIDsEqual(getCohortSegmentDataGroupId(item), dataGroupId);
}

function sanitizeQueryForDataGroup({
  query,
  dataGroupId,
  customEvents,
}: {
  query: QueryBuilderSelection;
  dataGroupId?: string;
  customEvents: CustomEventDefinition[];
}): QueryBuilderSelection {
  return {
    ...query,
    entries: sanitizeEventsForDataGroup({queryEntries: query.entries, dataGroupId, customEvents}),
    exclusionSteps: sanitizeEventsForDataGroup({
      queryEntries: query.exclusionSteps,
      dataGroupId,
      customEvents,
    }) as QueryEventEntry[],
    filters: query.filters.filter((filter) => !filterIsInvalidForDataGroup({filter, dataGroupId, customEvents})),
    groupBy: sanitizeGroupByForDataGroup(query.groupBy, dataGroupId),
  };
}

function sanitizeEventsForDataGroup({
  queryEntries,
  dataGroupId,
  customEvents,
}: {
  queryEntries: QueryEntry[];
  dataGroupId?: string;
  customEvents: CustomEventDefinition[];
}): QueryEntry[] {
  // remove invalid event filters and property aggregations
  const sanitizedQueryEntries = queryEntries.map((entry) => {
    if (isEventBasedType(entry.type) || isUserBasedType(entry)) {
      return {
        ...entry,
        filters: entry.filters.filter((filter) => !filterIsInvalidForDataGroup({filter, dataGroupId, customEvents})),
        property: propertyIsInvalidForDataGroup(entry.property, dataGroupId) ? null : entry.property,
      };
    } else {
      return entry;
    }
  });
  return sanitizedQueryEntries.filter((queryEntry) => {
    return !queryEntryIsInvalidForDataGroup({queryEntry, dataGroupId, customEvents});
  });
}

function queryEntryIsInvalidForDataGroup({
  queryEntry,
  dataGroupId,
  customEvents,
}: {
  queryEntry: QueryEntry;
  dataGroupId?: string;
  customEvents: CustomEventDefinition[];
}): boolean {
  switch (queryEntry.type) {
    case ResourceType.Event:
    case SelectorTypeDefinitions.FlowsEvent:
    case SelectorTypeDefinitions.Behavior: {
      return (
        (dataGroupId && isCustomEventWithUserFilters(queryEntry as QueryEventEntry, customEvents)) ||
        queryEntry.filters.some((filter) => filterIsInvalidForDataGroup({filter, dataGroupId, customEvents})) ||
        (queryEntry.property && propertyIsInvalidForDataGroup(queryEntry.property, dataGroupId))
      );
    }
    case SelectorTypeDefinitions.CohortGroup:
    case ResourceType.User: {
      const isAllPeople = `$all_people` === queryEntry.event.value;
      return (
        (!isAllPeople &&
          queryEntry.property &&
          profilePropertyIsInvalidForDataGroup({
            profilePropertyDataGroupId: queryEntry.dataGroupId,
            profilePropertyName: queryEntry.property.name,
            dataGroupId,
          })) ||
        queryEntry.filters.some((filter) => filterIsInvalidForDataGroup({filter, dataGroupId, customEvents}))
      );
    }
    case ResourceType.Cohort:
      return cohortEntryIsInvalidForDataGroup(queryEntry, dataGroupId);
    case SelectorTypeDefinitions.GroupedEvent:
      return queryEntry.event.some((entry) =>
        queryEntryIsInvalidForDataGroup({queryEntry: entry, dataGroupId, customEvents}),
      );
    default:
      // Log unhandled cases and default to marking as invalid for non-null data group
      reporter.error(`Unexpected QueryEntry.type: ${JSON.stringify(queryEntry)}`);
      return !!dataGroupId;
  }
}

function filterIsInvalidForDataGroup({
  filter,
  dataGroupId,
  customEvents,
}: {
  filter: Partial<PropertyFilter>;
  dataGroupId?: string;
  customEvents: CustomEventDefinition[];
}): boolean {
  switch (filter.resourceType) {
    case ResourceType.User: {
      if (filter.customProperty && isBehavioralCustomProperty(filter.customProperty)) {
        const {behavior} = filter.customProperty;
        return queryEntryIsInvalidForDataGroup({
          queryEntry: {...behavior, type: SelectorTypeDefinitions.Behavior, filters: behavior.filters ?? []},
          dataGroupId,
          customEvents,
        });
      }

      // TODO remove when behaviors are no longer user resource type
      if (isBehaviorSegmentation(filter as Segmentation)) {
        return false;
      }

      return profilePropertyIsInvalidForDataGroup({
        profilePropertyDataGroupId: filter.dataGroupId,
        profilePropertyName: filter.propertyName,
        dataGroupId,
      });
    }
    case ResourceType.Cohort:
      return (filter.filterValue as CohortSegment[]).some((s) => cohortEntryIsInvalidForDataGroup(s, dataGroupId));
    case ResourceType.Event:
    case ResourceType.Other:
      return false;
  }
}

function sanitizeGroupByForDataGroup(segments: Segment[], dataGroupId: string): Segment[] {
  const filteredSegments = segments.filter((s) => !segmentItemIsInvalidForDataGroup(s, dataGroupId));
  return filteredSegments;
}

function groupByIsInvalidForDataGroup(segments: Segment[], dataGroupId: string) {
  return segments.some((segment) => segmentItemIsInvalidForDataGroup(segment, dataGroupId));
}

function segmentItemIsInvalidForDataGroup(segment: Segment, dataGroupId: string): boolean {
  // TODO remove once behaviors are not user resource type
  if (isUserSegment(segment) && !isBehaviorSegmentation(segment.user)) {
    return [].concat(segment).some((seg) =>
      profilePropertyIsInvalidForDataGroup({
        profilePropertyDataGroupId: seg.user.dataGroupId,
        profilePropertyName: seg.user.propertyName,
        dataGroupId,
      }),
    );
  } else if (isCohortSegment(segment)) {
    return segment.some((item) => cohortEntryIsInvalidForDataGroup(item, dataGroupId));
  } else {
    // EventSegment, OtherSegment
    return false;
  }
}

function setQueryDataGroupProps({
  query,
  dataGroupId,
}: {
  query: QueryBuilderSelection;
  dataGroupId?: string;
}): QueryBuilderSelection {
  return {
    ...query,
    entries: query.entries.map((entry) => ({...entry, dataGroupId})),
    exclusionSteps: query.exclusionSteps.map((entry) => ({...entry, dataGroupId})),
    dataGroupId,
  };
}

export function profilePropertyIsInvalidForDataGroup({
  profilePropertyDataGroupId,
  profilePropertyName,
  dataGroupId,
}: {
  profilePropertyDataGroupId: string;
  profilePropertyName: string | string[];
  dataGroupId: string;
}) {
  if (profilePropertyName === `$mp_first_event_time`) {
    // First event time is a behavioral property, so it can only work
    // when the data group of the query matches the data group of the property
    // if we add support for b2b behavioral properties they will need to
    // be removed here as well
    return profilePropertyDataGroupId !== dataGroupId;
  } else if (dataGroupId) {
    // B2B analysis allows using all data groups' profile properties except for the empty "user" data group
    return !profilePropertyDataGroupId;
  } else {
    // User-level analysis can use all data groups' profile properties
    return false;
  }
}

function convertCohortItemToCohortSegment(item: CohortItem): CohortSegment {
  if (isInlineCohortItem(item)) {
    return {cohort: {...item.value.cohort, negated: false}};
  } else {
    return {
      cohort: {
        id: item.value as number,
        name: item.label,
        negated: false,
        ...(item.data_group_id ? {data_group_id: item.data_group_id} : null),
      },
    };
  }
}

function getCohortSegmentDataGroupId(segment: CohortSegment): string {
  if (isInlineCohortSegment(segment)) {
    return segment.cohort.raw_cohort.data_group_id;
  } else {
    return segment.cohort.data_group_id;
  }
}

function propertyIsInvalidForDataGroup(propertyItem?: PropertySectionItem, dataGroupId?: string): boolean {
  return (
    !!propertyItem &&
    propertyItem.resourceType === ResourceType.User &&
    profilePropertyIsInvalidForDataGroup({
      profilePropertyDataGroupId: propertyItem.dataGroupId,
      profilePropertyName: propertyItem.name,
      dataGroupId,
    })
  );
}
